import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import { AccountDetailsForm as GenericAccountDetailsForm } from 'components/generic';
import { useSendEventToSegment } from 'hooks/useSendEventToSegment/useSendEventToSegment';
import { useAuth0 } from 'hooks/Auth0/Auth0';
import { useGetNextPageUrl } from 'hooks/useGetNextPage/useGetNextPageUrl';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { useAccountSetupEvent } from 'hooks/GTMAnalytics/useGTMAnalytics';
import {
  useAddShippingInfoEventGA4,
  useCheckoutSignInEventGA4,
  usePartnerSignupAccountInfoEventGA4,
  useQuoteAcceptAccountInfoEventGA4
} from 'hooks/GTM4Analytics/useGTMAnalytics';
import { AccountDetail } from 'contexts/sessionProvider/sessionType';
import { useSegmentContext } from 'contexts/segmentProvider/SegmentProvider';
import {
  COOKIE_AJS_ANONYMOUS_ID,
  COOKIE_AJS_USER_ID,
  COOKIE_MYOB_VISITOR_ID,
  FLOW_STEPS,
  GTM_ANALYTICS_CONSTANTS,
  SUBSCRIPTION_TYPE
} from '../../constants';
import '../../app/App.scss';

export const AccountDetailsForm: React.FC<{
  submitButtonText: string
  isNewUser: boolean
  emailValue: string
}> = ({
  submitButtonText, isNewUser, emailValue
}) => {
  const {
    subscriptionType, setAccountDetail, accountDetail,
    quoteDetails
  } = useSessionContext();
  const history = useHistory();

  const disabledFields = subscriptionType === SUBSCRIPTION_TYPE.QUOTE ? {
    phoneNumber: true,
    businessName: true
  } : undefined;

  const [isTermsCheck, setIsTermsCheck] = React.useState(false);
  const [termsErrorMessage, setTermsErrorMessage] = React.useState('');
  const [sendPromotionsCheck, setSendPromotionsCheck] = React.useState(true);
  const [isSubmit, setIsSubmit] = useState(false);
  const setTerms = () => {
    setIsTermsCheck(!isTermsCheck);
    setTermsErrorMessage('');
  };
  const setSendPromotions = () => {
    setSendPromotionsCheck(!sendPromotionsCheck);
  };
  const { user } = useAuth0();
  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      email: emailValue,
      firstName: user?.given_name || '',
      lastName: user?.family_name || '',
      ...(subscriptionType === SUBSCRIPTION_TYPE.QUOTE && {
        phoneNumber: quoteDetails?.phoneNumber,
        businessName: quoteDetails?.businessName
      })
    }
  });

  const nextPageUrl = useGetNextPageUrl(FLOW_STEPS.ACCOUNT_SETUP, isNewUser);

  const postDirectDebitEvent = () => {
    const checkTerms = subscriptionType === SUBSCRIPTION_TYPE.SUBSCRIBE || subscriptionType === SUBSCRIPTION_TYPE.QUOTE;
    if (checkTerms && !isTermsCheck) {
      setTermsErrorMessage("Please confirm you've acknowledged the terms of use");
    } else {
      handleSubmit(onSubmit)();
    }
  };
  const {
    businessName, phoneNumber
  } = getValues();
  const identifyParamsInitial = {
    email: emailValue,
    last_name: user?.family_name,
    first_name: user?.given_name
  };
  const identifyParamsFormSubmit = {
    ...identifyParamsInitial,
    business_name: businessName,
    phone_number: phoneNumber,
    terms_and_conditions: isTermsCheck,
    communication_preference: sendPromotionsCheck
  };

  const anonymousId = Cookies.get(COOKIE_AJS_ANONYMOUS_ID);
  const myobVisitorId = Cookies.get(COOKIE_MYOB_VISITOR_ID);
  const userId = Cookies.get(COOKIE_AJS_USER_ID);

  const identifyParams = {
    userId,
    anonymousId,
    traits: {
      ...identifyParamsInitial,
      ...identifyParamsFormSubmit,
      myobVisitorId
    }
  };

  const trackParams = {
    event: GTM_ANALYTICS_CONSTANTS.ACCOUNT_SETUP_SEGMENT,
    userId,
    anonymousId,
    properties: {
      myobVisitorId
    }
  };
  const isSegmentLibraryReady = useSegmentContext();

  useAccountSetupEvent(isSegmentLibraryReady);
  usePartnerSignupAccountInfoEventGA4(isSegmentLibraryReady);
  useQuoteAcceptAccountInfoEventGA4(isSegmentLibraryReady);
  useAccountSetupEvent(
    isSegmentLibraryReady && isSubmit,
    identifyParamsFormSubmit,
    GTM_ANALYTICS_CONSTANTS.ACCOUNT_SETUP_SEGMENT
  );
  useAccountSetupEvent(
    isSegmentLibraryReady,
    identifyParamsInitial,
    GTM_ANALYTICS_CONSTANTS.ACCOUNT_SETUP_SEGMENT
  );
  useAddShippingInfoEventGA4(isSegmentLibraryReady, isNewUser);
  useCheckoutSignInEventGA4(
    isSubmit && subscriptionType === SUBSCRIPTION_TYPE.TRIAL && isSegmentLibraryReady,
    isNewUser
  );
  useSendEventToSegment({
    shouldSendSegmentEvent: isSubmit,
    identifyParams,
    trackParams
  });

  // redirect to next page when accountDetail is set
  // to avoid breaking analytics event hooks firing
  useEffect(() => {
    if (accountDetail) {
      history.push(nextPageUrl);
    }
  }, [accountDetail]);

  const onSubmit = async () => {
    setIsSubmit(true);

    const formData = getValues();

    setAccountDetail({
      firstName: user?.given_name || formData.firstName,
      lastName: user?.family_name || formData.lastName,
      phoneNumber: formData.phoneNumber,
      businessName: formData.businessName,
      sendPromotions: sendPromotionsCheck
    } as AccountDetail);
  };

  const shouldShowNames = !user?.given_name && !user?.family_name;

  return (
    <GenericAccountDetailsForm
      { ...shouldShowNames && { shouldShowNames } }
      disabledSubmitBtn={isSubmit}
      handleSubmit={postDirectDebitEvent}
      // todo: get rid of submitButtonText prop
      submitButtonText={submitButtonText}
      isTermsCheck={isTermsCheck}
      termsErrorMessage={termsErrorMessage}
      setTerms={setTerms}
      sendPromotionsCheck={sendPromotionsCheck}
      setSendPromotions={setSendPromotions}
      register={register}
      errors={errors}
      disabledFields={disabledFields}
    />
  );
};
